import React, { createContext, ReactNode, useContext } from "react";
import { ApolloError } from "@apollo/client";
// eslint-disable-next-line no-restricted-imports
import { useSession as useSessionHook } from "@src/components/modules/Header/useSession";
import { VendorTokenData } from "@src/sudozi-schemas/token-data-schemas";
import {
  ApiOutputSchema,
  GetCurrentCompanyRoute,
  GetCurrentUserCompanyMembershipsRoute,
} from "@src/sudozi-schemas/api-route-schemas";

type ErpIntegrationType =
  | "NETSUITE"
  | "QUICKBOOKS"
  | "TRIPADVISOR_CUSTOM_SYNC"
  | null;

interface UseSessionType {
  loading: boolean;
  currentCompany?: ApiOutputSchema<typeof GetCurrentCompanyRoute>;
  companyMemberships: NonNullable<
    ApiOutputSchema<typeof GetCurrentUserCompanyMembershipsRoute>
  >;
  error?: ApolloError;
  enableAutoCompanyLogos: boolean;
  enableIntakeRequests: boolean;
  enableSpendRequests: boolean;
  enableVendorIntake: boolean;
  enableN12mServiceSpend: boolean;
  enableNetsuitePurchaseOrder: boolean;
  enableEditWithApprovalBypass: boolean;
  enableCurrencies: boolean;
  enableSourcing: boolean;
  enableBusinessUnits: boolean;
  enableDocumentSummary: boolean;
  hasErpClassifications: boolean;
  paymentsEnabled: boolean;
  enablePaymentAccounting: boolean;
  paymentsOnboarded: boolean;
  enforceIntakeApprovalOrder: boolean;
  submitsSpendRequestsForOthers: boolean;
  vendorUser: VendorTokenData | null;
  vendorCompany?: {
    id: string;
    name: string;
    vendorType: "BUSINESS" | "INDIVIDUAL";
  };
  refetchVendorCompanyData: () => void;
  hasAdminAccess: boolean;
  approvesRequestsForBusinessUnit: boolean;
  approvesRequestsForDepartment: boolean;
  hasApprovalBypassAccess: boolean;
  hasBusinessUnitAccess: boolean;
  hasBusinessUnitPayrollAccess: boolean;
  hasDepartmentAccess: boolean;
  hasDepartmentPayrollAccess: boolean;
  hasFinancialAnalysisAccess: boolean;
  hasPaymentsAccess: boolean;
  hasSuperViewerAccess: boolean;
  hasVendorAdminAccess: boolean;
  hasUserAdminAccess: boolean;
  hasSpendRequestAccess: boolean;
  isRequestCategoryOwner: boolean;
  ironcladCompanyId: string | null;
  userId: string;
  departmentIds: string[];
  businessUnitIds: string[];
  locationId: string | null;
  groupIds: string[];
  refetchPermissions: () => void;
  refetchCompany: () => void;
  applicationHostname: string;
  jiraBaseUrl: string | null;
  erpIntegrationType: ErpIntegrationType;
  enableEntities: boolean;
  restrictSubmissionEditToAdmins: boolean;
}

const sessionContext = createContext<UseSessionType>({
  loading: true,
  currentCompany: undefined,
  companyMemberships: [],
  error: undefined,
  enableAutoCompanyLogos: false,
  enableIntakeRequests: false,
  enableSpendRequests: false,
  enableVendorIntake: false,
  enableEditWithApprovalBypass: false,
  enableCurrencies: false,
  enableSourcing: false,
  enableBusinessUnits: false,
  enableDocumentSummary: false,
  enforceIntakeApprovalOrder: false,
  enableN12mServiceSpend: false,
  enableNetsuitePurchaseOrder: false,
  paymentsEnabled: false,
  enablePaymentAccounting: false,
  paymentsOnboarded: false,
  hasErpClassifications: false,
  submitsSpendRequestsForOthers: false,
  vendorUser: null,
  vendorCompany: undefined,
  refetchVendorCompanyData: () => undefined,
  hasAdminAccess: false,
  approvesRequestsForBusinessUnit: false,
  approvesRequestsForDepartment: false,
  hasApprovalBypassAccess: false,
  hasBusinessUnitAccess: false,
  hasBusinessUnitPayrollAccess: false,
  hasDepartmentAccess: false,
  hasDepartmentPayrollAccess: false,
  hasFinancialAnalysisAccess: false,
  hasPaymentsAccess: false,
  hasSuperViewerAccess: false,
  hasVendorAdminAccess: false,
  hasUserAdminAccess: false,
  hasSpendRequestAccess: false,
  isRequestCategoryOwner: false,
  ironcladCompanyId: null,
  userId: "",
  departmentIds: [],
  businessUnitIds: [],
  locationId: null,
  groupIds: [],
  refetchPermissions: () => undefined,
  refetchCompany: () => undefined,
  applicationHostname: "",
  jiraBaseUrl: null,
  erpIntegrationType: null,
  enableEntities: false,
  restrictSubmissionEditToAdmins: false,
});

export const SessionProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  return (
    <sessionContext.Provider value={useSessionHook()}>
      {children}
    </sessionContext.Provider>
  );
};

export const useSession = (): UseSessionType => {
  return useContext(sessionContext);
};
